import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import { useWorkspace } from '@/lib/workspace-context'

export interface EventExhibitor {
  id: string
  userId: string
  email: string
  name: string
  assignedAt: string
  profileSlug: string | null
  companyName: string | null
}

export interface AvailableExhibitor {
  userId: string
  email: string
  name: string
  role: 'owner' | 'admin' | 'user'
  alreadyAssigned: boolean
}

/**
 * Alle Aussteller die einem Event zugeordnet sind
 */
export function useEventExhibitors(eventId: string) {
  return useQuery({
    queryKey: ['event-exhibitors', eventId],
    queryFn: async (): Promise<EventExhibitor[]> => {
      const { data, error } = await supabase
        .from('event_exhibitors')
        .select(`
          id,
          user_id,
          assigned_at,
          user_profiles (
            email,
            name
          )
        `)
        .eq('event_id', eventId)
        .order('assigned_at', { ascending: true })

      if (error) throw error

      // Exhibitor-Profile separat laden (kein FK-Join möglich)
      const userIds = (data || []).map((r) => r.user_id)
      const { data: profiles } = userIds.length > 0
        ? await supabase
            .from('exhibitor_profiles')
            .select('user_id, slug, company_name')
            .in('user_id', userIds)
        : { data: [] }

      const profileMap = new Map(
        (profiles || []).map((p) => [p.user_id, p])
      )

      return (data || []).map((row) => {
        const profile = row.user_profiles as { email: string; name: string }
        const exProfile = profileMap.get(row.user_id)
        return {
          id: row.id,
          userId: row.user_id,
          email: profile?.email || '',
          name: profile?.name || '',
          assignedAt: row.assigned_at,
          profileSlug: exProfile?.slug || null,
          companyName: exProfile?.company_name || null,
        }
      })
    },
    enabled: !!eventId,
  })
}

/**
 * Alle Aussteller des Workspaces mit Info ob sie dem Event schon zugeordnet sind
 */
export function useAvailableExhibitors(eventId: string) {
  const { activeWorkspace } = useWorkspace()

  return useQuery({
    queryKey: ['available-exhibitors', activeWorkspace?.id, eventId],
    queryFn: async (): Promise<AvailableExhibitor[]> => {
      if (!activeWorkspace) return []

      // Alle Mitglieder des Workspace
      const { data: members, error: membersError } = await supabase
        .from('tenant_users')
        .select(`
          user_id,
          role,
          user_profiles (
            email,
            name
          )
        `)
        .eq('tenant_id', activeWorkspace.id)

      if (membersError) throw membersError

      // Bereits zugeordnete
      const { data: assigned } = await supabase
        .from('event_exhibitors')
        .select('user_id')
        .eq('event_id', eventId)

      const assignedIds = new Set((assigned || []).map((a) => a.user_id))

      return (members || [])
        .filter((m) => m.role === 'user') // Nur Aussteller, keine Admins/Owner
        .map((m) => {
          const profile = m.user_profiles as { email: string; name: string }
          return {
            userId: m.user_id,
            email: profile?.email || '',
            name: profile?.name || '',
            role: m.role as AvailableExhibitor['role'],
            alreadyAssigned: assignedIds.has(m.user_id),
          }
        })
        .sort((a, b) => {
          // Nicht-zugeordnete zuerst
          if (a.alreadyAssigned !== b.alreadyAssigned) return a.alreadyAssigned ? 1 : -1
          return (a.name || a.email).localeCompare(b.name || b.email)
        })
    },
    enabled: !!activeWorkspace && !!eventId,
  })
}

/**
 * Aussteller einem Event zuordnen
 */
export function useAssignExhibitor() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({ eventId, userId }: { eventId: string; userId: string }) => {
      if (!user) throw new Error('Nicht angemeldet')

      const { error } = await supabase.from('event_exhibitors').insert({
        event_id: eventId,
        user_id: userId,
        assigned_by: user.id,
      })

      if (error) throw error
    },
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['event-exhibitors', eventId] })
      queryClient.invalidateQueries({ queryKey: ['available-exhibitors'] })
    },
  })
}

/**
 * Mehrere Aussteller auf einmal zuordnen
 */
export function useBulkAssignExhibitors() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({ eventId, userIds }: { eventId: string; userIds: string[] }) => {
      if (!user) throw new Error('Nicht angemeldet')

      const { error } = await supabase.from('event_exhibitors').insert(
        userIds.map((userId) => ({
          event_id: eventId,
          user_id: userId,
          assigned_by: user.id,
        }))
      )

      if (error) throw error
    },
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['event-exhibitors', eventId] })
      queryClient.invalidateQueries({ queryKey: ['available-exhibitors'] })
    },
  })
}

/**
 * Aussteller von Event entfernen
 */
export function useRemoveExhibitor() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, eventId }: { id: string; eventId: string }) => {
      const { error } = await supabase
        .from('event_exhibitors')
        .delete()
        .eq('id', id)

      if (error) throw error
      return eventId
    },
    onSuccess: (eventId) => {
      queryClient.invalidateQueries({ queryKey: ['event-exhibitors', eventId] })
      queryClient.invalidateQueries({ queryKey: ['available-exhibitors'] })
    },
  })
}
